import { useCallback, useEffect, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { InfoOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, NumberInput, Stack, Tooltip, Typography } from '@lyearn/ui';
import SwitchWithInfo from '@/components/SwitchWithInfo';
import { ReviewerConfigInput, ReviewerTypeEnum } from '@/types/schema';

import { validateReviewerPeerError } from '../utils';

const REVIEW_REQUIRED_TOGGLE_CLASSES = {
  container: 'mx-8 mt-24',
};

const DEFAULT_MINIMUM_REVIEWERS = 1;

function SelectPeer({
  reviewerConfig,
  showError,
  onReviewerConfigChange,
  isEditMode,
  initialReviewerConfig,
  className,
}: {
  reviewerConfig: ReviewerConfigInput;
  showError?: boolean;
  onReviewerConfigChange: (nextValue: ReviewerConfigInput) => void;
  isEditMode: boolean;
  initialReviewerConfig: ReviewerConfigInput;
  className?: string;
}) {
  const { t } = useTranslation('Group');
  const { minimumReviewsRequired, anonymousReview } = reviewerConfig;
  const [peerConfig, setPeerConfig] = useState({
    minimumReviewsRequired: minimumReviewsRequired ?? DEFAULT_MINIMUM_REVIEWERS,
    anonymousReview: anonymousReview ?? true,
  });

  const onPeerReviewChange = useCallback(
    (value: number) => {
      setPeerConfig((config) => ({ ...config, minimumReviewsRequired: value }));
      onReviewerConfigChange({ ...reviewerConfig, minimumReviewsRequired: value });
    },
    [onReviewerConfigChange, reviewerConfig],
  );

  const onPeerAnonymityChange = useCallback(
    (value: boolean) => {
      setPeerConfig((config) => ({ ...config, anonymousReview: value }));
      onReviewerConfigChange({ ...reviewerConfig, anonymousReview: value });
    },
    [onReviewerConfigChange, reviewerConfig],
  );

  const error = Boolean(showError && validateReviewerPeerError(reviewerConfig));

  useEffect(() => {
    if (minimumReviewsRequired == null) {
      onPeerReviewChange(DEFAULT_MINIMUM_REVIEWERS);
    }
    if (typeof anonymousReview !== 'boolean') {
      onPeerAnonymityChange(true);
    }
  }, [anonymousReview, minimumReviewsRequired, onPeerAnonymityChange, onPeerReviewChange]);

  return (
    <Stack className="w-full" direction="col">
      <Box className={cn('space-y-8', className)}>
        <Stack alignItems="center" className="ms-8">
          <Typography color="text-text-primary" variant="body-short-02">
            {t('Exercise:Creator.ReviewsLearner')}
          </Typography>
          <Tooltip placement="right" title={t('Exercise:Creator.PeerReviewerTooltipInfo')!}>
            <Box>
              <InfoOutline className="h-16 w-16 ms-4" fillColorToken="icon-secondary" />
            </Box>
          </Tooltip>
        </Stack>
        <Box className="flex flex-1 items-center">
          <NumberInput
            canBeEmpty
            className="w-full"
            disabled={isEditMode && initialReviewerConfig.type === ReviewerTypeEnum.Peer}
            error={error}
            inputSuffix={t('Exercise:Creator.Reviews')}
            max={9}
            min={1}
            value={peerConfig.minimumReviewsRequired}
            onChange={onPeerReviewChange}
          />
        </Box>
        {error ? (
          <Typography className="ms-8" color="text-text-error" variant="body-short-03">
            {validateReviewerPeerError(reviewerConfig)}
          </Typography>
        ) : null}
      </Box>
      <SwitchWithInfo
        checked={peerConfig.anonymousReview}
        classes={REVIEW_REQUIRED_TOGGLE_CLASSES}
        description={t('Exercise:Creator.AnonymousReviewingDescription')}
        disabled={isEditMode && initialReviewerConfig.type === ReviewerTypeEnum.Peer}
        label={t('Exercise:Creator.AnonymousReviewing')}
        onChange={onPeerAnonymityChange}
      />
    </Stack>
  );
}

export default SelectPeer;
